import { BasePageDto } from "@/types/dto/base-page-dto";
import { BaseResponse } from "@/types/dto/base-response";
import { ItemDetailDto } from "@/types/dto/item-detail-dto";
import {
  ItemPageAdminPayload,
  ItemPagePartnerPayload,
  ItemPagePayload,
} from "@/types/item/payload/item-page-payload";
import { CreateItemPayload } from "@/types/payload/create-item-payload";
import { IdPayload } from "@/types/payload/id-payload";
import { ItemPageByCategoryPayload } from "@/types/payload/item-page-by-category-payload";
import { ManageItemPayload } from "@/types/payload/manage-item-payload";
import { UpdateItemDiscountsPayload } from "@/types/payload/update-item-discounts-payload";
import { UpdateItemPayload } from "@/types/payload/update-item-payload";
import { AxiosInstance } from "axios";
import { ErpDataDto } from "../types/dto/erp-data-dto";
import { HistoryDto } from "../types/dto/history-dto";
import { CreatePackagePayload } from "../types/payload/create-package-payload";
import { ErpPagePayload } from "../types/payload/erp-page-payload";
import { UpdatePackagePayload } from "../types/payload/update-package-payload";
import { baseApi } from "./base-api";

enum ItemServiceEndpoint {
  BaseV3 = "/v3/item",
  Create = "/v3/item",
  CreatePackage = "/v3/item/package",
  GetById = "/v3/item",
  GetPage = "/v3/items",
  GetPartnerPage = "/v3/items",
  GetPageByCategory = "/v3/items/category",
  UpdateDiscounts = "/v3/item/discounts",
  Update = "/v3/item",
  UpdatePackage = "/v3/item/package",
  Delete = "/v3/item",
  Manage = "/item/manage",
  ErpPage = "/v3/item/erp-code",
  Star = "/star",
  DeleteStar = "/star",
}

class ItemService {
  private static instance: ItemService;
  private _api: AxiosInstance;

  private constructor(api: AxiosInstance) {
    this._api = api;
  }

  static getInstance(apiClient = baseApi): ItemService {
    if (!ItemService.instance) {
      ItemService.instance = new ItemService(apiClient);
    }
    return ItemService.instance;
  }

  async create(
    payload: CreateItemPayload
  ): Promise<BaseResponse<ItemDetailDto, CreateItemPayload>> {
    const formData = new FormData();
    formData.append("name", payload.name);
    formData.append("categoryId", payload.categoryId.toString());
    formData.append("details", payload.details);
    formData.append("features", payload.features);
    formData.append("modelName", payload.modelName);
    formData.append("erpCode", payload.erpCode);
    formData.append("memos", JSON.stringify(payload.memos));
    formData.append("isCataloged", payload.isCataloged.toString());
    formData.append("link", payload.link);

    payload.files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await this._api.post(
        ItemServiceEndpoint.Create,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error create:", error);
      throw error;
    }
  }
  async createPackage(
    payload: CreatePackagePayload
  ): Promise<BaseResponse<ItemDetailDto, CreatePackagePayload>> {
    const formData = new FormData();
    formData.append("name", payload.name);
    formData.append("details", payload.details);
    formData.append("features", payload.features);
    formData.append("modelName", payload.modelName);
    formData.append("isCataloged", payload.isCataloged.toString());

    payload.files.forEach((file) => {
      formData.append("files", file);
    });

    formData.append("items", JSON.stringify(payload.items));
    formData.append("memos", JSON.stringify(payload.memos));

    try {
      const response = await this._api.post(
        ItemServiceEndpoint.CreatePackage,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error create:", error);
      throw error;
    }
  }

  async getById(
    payload: IdPayload
  ): Promise<BaseResponse<ItemDetailDto, void>> {
    try {
      const response = await this._api.get(
        `${ItemServiceEndpoint.GetById}/${payload.id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error getById:", error);
      throw error;
    }
  }

  async getPage(
    payload: ItemPagePayload
  ): Promise<BaseResponse<BasePageDto<ItemDetailDto>, ItemPagePayload>> {
    try {
      const params: Record<string, any> = {
        page: payload.page,
        size: payload.size,
        isCategoryInitialOrder: payload.isCategoryInitialOrder,
        isForSale: payload.isForSale,
        isDisplayed: payload.isDisplayed,
        name: payload.name || "",
      };

      if (payload.categoryIds && payload.categoryIds.length > 0) {
        params.categoryIds = String(payload.categoryIds.join(";"));
      }

      const response = await this._api.get<
        BaseResponse<BasePageDto<ItemDetailDto>, ItemPagePayload>
      >(ItemServiceEndpoint.GetPage, { params });

      return response.data;
    } catch (error) {
      console.error("Error getPage:", error);
      throw error;
    }
  }

  async getPageAdmin(
    payload: ItemPageAdminPayload
  ): Promise<BaseResponse<BasePageDto<ItemDetailDto>, ItemPageAdminPayload>> {
    try {
      const params: Record<string, any> = {
        page: payload.page,
        size: payload.size,
        name: payload.name || "",
        orderType: payload.orderType,
        itemType: payload.itemType,
      };

      if (payload.categoryIds && payload.categoryIds.length > 0) {
        params.categoryIds = String(payload.categoryIds.join(";"));
      }

      const response = await this._api.get<
        BaseResponse<BasePageDto<ItemDetailDto>, ItemPageAdminPayload>
      >(ItemServiceEndpoint.GetPage, { params });

      return response.data;
    } catch (error) {
      console.error("Error getPage:", error);
      throw error;
    }
  }

  async getPagePartner(
    id: number,
    payload: ItemPagePartnerPayload
  ): Promise<BaseResponse<BasePageDto<ItemDetailDto>, ItemPagePartnerPayload>> {
    try {
      const params: Record<string, any> = {
        page: payload.page,
        size: payload.size,
        name: payload.name || "",
        orderType: payload.orderType,
        itemType: payload.itemType,
      };

      if (payload.categoryIds && payload.categoryIds.length > 0) {
        params.categoryIds = String(payload.categoryIds.join(";"));
      }

      if (payload.isStar) {
        params.isStar = payload.isStar;
      }

      const response = await this._api.get<
        BaseResponse<BasePageDto<ItemDetailDto>, ItemPagePartnerPayload>
      >(`${ItemServiceEndpoint.GetPartnerPage}/${id}`, { params });

      return response.data;
    } catch (error) {
      console.error("Error getPage:", error);
      throw error;
    }
  }

  async getPageByCategory(
    payload: ItemPageByCategoryPayload
  ): Promise<BaseResponse<BasePageDto<ItemDetailDto>, void>> {
    try {
      const params: Record<string, any> = {
        page: payload.page,
        size: payload.size,
      };

      if (payload.categoryIds && payload.categoryIds.length > 0) {
        params.categoryIds = String(payload.categoryIds.join(";"));
      }

      const response = await this._api.get<
        BaseResponse<BasePageDto<ItemDetailDto>, void>
      >(ItemServiceEndpoint.GetPageByCategory, { params });
      return response.data;
    } catch (error) {
      console.error("Error getPageByCategory:", error);
      throw error;
    }
  }

  async updateDiscounts(
    payload: UpdateItemDiscountsPayload
  ): Promise<BaseResponse<void, void>> {
    try {
      const response = await this._api.patch<BaseResponse<void, void>>(
        ItemServiceEndpoint.UpdateDiscounts,
        {
          ids: payload.ids,
          discountRate: payload.discountRate,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updateDiscounts:", error);
      throw error;
    }
  }

  async update(
    id: number,
    payload: UpdateItemPayload
  ): Promise<BaseResponse<ItemDetailDto, UpdateItemPayload>> {
    const formData = new FormData();
    formData.append("name", payload.name);
    formData.append("categoryId", payload.categoryId.toString());
    formData.append("details", payload.details);
    formData.append("features", payload.features);
    formData.append("modelName", payload.modelName);
    formData.append("erpCode", payload.erpCode);
    formData.append("isCataloged", payload.isCataloged.toString());
    formData.append("minOrderQuantity", payload.minOrderQuantity.toString());
    formData.append("price", payload.price.toString());
    formData.append("discountRate", payload.discountRate.toString());
    formData.append("isForSale", payload.isForSale.toString());
    formData.append("isDisplayed", payload.isDisplayed.toString());
    formData.append("memos", JSON.stringify(payload.memos));
    formData.append("link", payload.link);

    payload.files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await this._api.patch<
        BaseResponse<ItemDetailDto, UpdateItemPayload>
      >(`${ItemServiceEndpoint.Update}/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error update:", error);
      throw error;
    }
  }

  async updatePackage(
    id: number,
    payload: UpdatePackagePayload
  ): Promise<BaseResponse<ItemDetailDto, UpdatePackagePayload>> {
    const formData = new FormData();
    formData.append("name", payload.name);
    formData.append("details", payload.details);
    formData.append("features", payload.features);
    formData.append("modelName", payload.modelName);
    formData.append("isCataloged", payload.isCataloged.toString());
    formData.append("minOrderQuantity", payload.minOrderQuantity.toString());
    formData.append("price", payload.price.toString());
    formData.append("discountRate", payload.discountRate.toString());
    formData.append("isForSale", payload.isForSale.toString());
    formData.append("isDisplayed", payload.isDisplayed.toString());

    payload.files.forEach((file) => {
      formData.append("files", file);
    });

    formData.append("items", JSON.stringify(payload.items));
    formData.append("memos", JSON.stringify(payload.memos));

    try {
      const response = await this._api.patch<
        BaseResponse<ItemDetailDto, UpdatePackagePayload>
      >(`${ItemServiceEndpoint.UpdatePackage}/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error update:", error);
      throw error;
    }
  }

  async delete(payload: IdPayload): Promise<BaseResponse<void, void>> {
    try {
      const response = await this._api.delete<BaseResponse<void, void>>(
        `${ItemServiceEndpoint.Delete}/${payload.id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error delete:", error);
      throw error;
    }
  }

  async manage(
    id: number,
    payload: ManageItemPayload
  ): Promise<BaseResponse<void, ManageItemPayload>> {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, value]) => value !== undefined)
      );
      const response = await this._api.patch<
        BaseResponse<void, ManageItemPayload>
      >(
        ItemServiceEndpoint.BaseV3 + "/" + id + "/manage",
        {},
        {
          params: filteredPayload,
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error manage:", error);
      throw error;
    }
  }

  async getErpPage(
    payload: ErpPagePayload
  ): Promise<BaseResponse<BasePageDto<ErpDataDto>, ErpPagePayload>> {
    try {
      // const params: Record<string, any> = {
      //   page: payload.page,
      //   size: payload.size,
      //   isCategoryInitialOrder: payload.isCategoryInitialOrder,
      //   isForSale: payload.isForSale,
      //   isDisplayed: payload.isDisplayed,
      //   name: payload.name || "",
      // };

      const response = await this._api.get<
        BaseResponse<BasePageDto<ErpDataDto>, ErpPagePayload>
      >(ItemServiceEndpoint.ErpPage, { params: payload });

      return response.data;
    } catch (error) {
      console.error("Error getPage:", error);
      throw error;
    }
  }

  async star(id: number): Promise<BaseResponse<void, void>> {
    try {
      const response = await this._api.patch<BaseResponse<void, void>>(
        `${ItemServiceEndpoint.BaseV3}/${id}${ItemServiceEndpoint.Star}`
      );
      return response.data;
    } catch (error) {
      console.error("Error star:", error);
      throw error;
    }
  }

  async deleteStar(id: number): Promise<BaseResponse<void, void>> {
    try {
      const response = await this._api.delete<BaseResponse<void, void>>(
        `${ItemServiceEndpoint.BaseV3}/${id}${ItemServiceEndpoint.DeleteStar}`
      );
      return response.data;
    } catch (error) {
      console.error("Error deleteStar:", error);
      throw error;
    }
  }

  async getItemDetailPatchNote(
    id: number
  ): Promise<BaseResponse<HistoryDto[], void>> {
    try {
      const response = await this._api.get<BaseResponse<HistoryDto[], void>>(
        `/v3/item/${id}/patch-notes`
      );
      return response.data;
    } catch (error) {
      console.error("Error getItemDetailPatchNote:", error);
      throw error;
    }
  }
}

export const itemService = ItemService.getInstance(baseApi);
